import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Login({ onLogin }) {
  const [step, setStep] = useState('phone');
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSendOTP = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/send-otp`, { phone });
      const otp = response.data.otp;
      toast.success(`OTP sent successfully!`, {
        duration: 10000,
        description: `Demo OTP: ${otp} (Valid for testing only)`,
      });
      setStep('otp');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/verify-otp`, { phone, otp });
      toast.success('Login successful!');
      onLogin(response.data.token, response.data.user);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen flex">
      <div className="flex-1 relative overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: "url('https://images.unsplash.com/photo-1744421462895-f34a7cca3f04?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHwyfHxwcm9mZXNzaW9uYWwlMjBiYWtlcnklMjBraXRjaGVuJTIwY2hlZnxlbnwwfHx8fDE3NjkxOTUzNTZ8MA&ixlib=rb-4.1.0&q=85')",
          }}
        />
        <div className="absolute inset-0 bg-primary/80 backdrop-blur-sm" />
        <div className="relative h-full flex items-center justify-center p-12">
          <div className="text-white max-w-2xl text-center">
            <div className="flex justify-center mb-8">
              <img 
                src="https://customer-assets.emergentagent.com/job_roch-kitchen/artifacts/fdmjtm1k_logo%20%281%29.png" 
                alt="ROCH Cake Studio" 
                className="w-56 h-56 object-contain drop-shadow-2xl"
              />
            </div>
            <h1 className="font-heading text-5xl font-bold mb-4">ROCH Cake Studio</h1>
            <p className="text-xl text-stone-200 leading-relaxed mb-12">
              Central Kitchen & Outlet Management System
            </p>
            <div className="space-y-4 text-stone-300 text-left max-w-md mx-auto">
              <div className="flex items-center gap-3">
                <div className="w-2 h-2 bg-secondary rounded-full" />
                <span>Production Tracking</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-2 h-2 bg-secondary rounded-full" />
                <span>Inventory Management</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-2 h-2 bg-secondary rounded-full" />
                <span>Inter-Branch Transfers</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="w-full max-w-md bg-white flex items-center justify-center p-12">
        <div className="w-full">
          <h2 className="font-heading text-3xl font-bold text-stone-900 mb-2">Sign In</h2>
          <p className="text-stone-600 mb-8">Enter your phone number to continue</p>

          {step === 'phone' ? (
            <form onSubmit={handleSendOTP} data-testid="login-form">
              <div className="mb-6">
                <label className="block text-sm font-medium text-stone-700 mb-2">
                  Phone Number
                </label>
                <input
                  type="tel"
                  data-testid="phone-input"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="+91 1234567890"
                  required
                  className="w-full h-10 px-3 border border-stone-200 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
              <button
                type="submit"
                data-testid="send-otp-button"
                disabled={loading}
                className="w-full h-10 bg-secondary text-black rounded-sm font-semibold hover:bg-secondary-hover transition-colors disabled:opacity-50"
              >
                {loading ? 'Sending...' : 'Send OTP'}
              </button>

              <div className="flex items-center gap-3 my-5">
                <div className="flex-1 h-px bg-stone-200" />
                <span className="text-xs text-stone-400 uppercase tracking-wide">or</span>
                <div className="flex-1 h-px bg-stone-200" />
              </div>

              <button
                type="button"
                data-testid="google-login-button"
                onClick={handleGoogle}
                className="w-full h-10 border border-stone-300 rounded-sm font-medium text-stone-700 hover:bg-stone-50 transition-colors flex items-center justify-center gap-3"
              >
                <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
                  <path fill="#FFC107" d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8c-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4C12.955 4 4 12.955 4 24s8.955 20 20 20s20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z"/>
                  <path fill="#FF3D00" d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4C16.318 4 9.656 8.337 6.306 14.691z"/>
                  <path fill="#4CAF50" d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238A11.91 11.91 0 0 1 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z"/>
                  <path fill="#1976D2" d="M43.611 20.083H42V20H24v8h11.303a12.04 12.04 0 0 1-4.087 5.571l.003-.002l6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z"/>
                </svg>
                Continue with Google
              </button>
            </form>
          ) : (
            <form onSubmit={handleVerifyOTP} data-testid="otp-form">
              <div className="mb-2">
                <div className="bg-secondary/10 border border-secondary/30 rounded-sm p-3 mb-4">
                  <p className="text-sm text-stone-700">
                    <strong>Demo Mode:</strong> Check the notification at the top-right corner for your OTP code.
                  </p>
                </div>
              </div>
              <div className="mb-6">
                <label className="block text-sm font-medium text-stone-700 mb-2">
                  Enter OTP
                </label>
                <input
                  type="text"
                  data-testid="otp-input"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value)}
                  placeholder="123456"
                  maxLength={6}
                  required
                  className="w-full h-10 px-3 border border-stone-200 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary font-mono text-lg"
                />
              </div>
              <button
                type="submit"
                data-testid="verify-otp-button"
                disabled={loading}
                className="w-full h-10 bg-secondary text-black rounded-sm font-semibold hover:bg-secondary-hover transition-colors disabled:opacity-50 mb-3"
              >
                {loading ? 'Verifying...' : 'Verify OTP'}
              </button>
              <button
                type="button"
                data-testid="back-button"
                onClick={() => setStep('phone')}
                className="w-full h-10 border border-stone-200 text-stone-700 rounded-sm font-medium hover:bg-stone-50 transition-colors"
              >
                Back
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
